import React, { FC } from "react";
import FullCalendar from "@fullcalendar/react";
import { DatesSetArg, EventContentArg, ViewMountArg } from "@fullcalendar/core";
import { TFunction } from "i18next";
import {
  CALENDAR_PLUGINS,
  getCalendarToolbarOptions,
} from "../calendarToolbarConfig";
import { CalendarPeriod } from "../calendarPeriod";
import { CALENDAR_TIME_FORMAT } from "../constants";
import { CalendarEvent, TaskCalendarStatus } from "../types";
import { getScheduleEventColor } from "../utils";
import { CalendarEventContent } from "./CalendarEventContent";

interface TaskScheduleCalendarProps {
  events: CalendarEvent[];
  t: TFunction;
  fullHeight?: boolean;
  onDatesChange: (period: CalendarPeriod, start: Date, end: Date) => void;
  onEventClick?: (eventId: string) => void;
}

export const TaskScheduleCalendar: FC<TaskScheduleCalendarProps> = ({
  events,
  t,
  fullHeight,
  onDatesChange,
  onEventClick,
}) => {
  const syncView = (viewType: string, start: Date, end: Date) => {
    onDatesChange(viewType as CalendarPeriod, start, end);
  };

  const handleViewDidMount = (arg: ViewMountArg) => {
    syncView(arg.view.type, arg.view.currentStart, arg.view.currentEnd);
  };

  const handleDatesSet = (arg: DatesSetArg) => {
    syncView(arg.view.type, arg.start, arg.end);
  };

  return (
    <FullCalendar
      plugins={CALENDAR_PLUGINS}
      initialView="dayGridMonth"
      firstDay={1}
      {...getCalendarToolbarOptions(t)}
      events={events}
      dayMaxEvents={4}
      allDaySlot={false}
      slotEventOverlap={false}
      slotLabelFormat={CALENDAR_TIME_FORMAT}
      height={fullHeight ? "100vh" : "calc(100vh - 180px)"}
      viewDidMount={handleViewDidMount}
      datesSet={handleDatesSet}
      eventClick={
        onEventClick ? (arg) => onEventClick(arg.event.id) : undefined
      }
      eventClassNames={(arg) => {
        const { status } = arg.event.extendedProps as {
          status: TaskCalendarStatus;
        };
        const { bg, text } = getScheduleEventColor(status);
        return [
          "rounded px-1 py-0.5 text-[11px] leading-tight font-semibold shadow-sm",
          onEventClick ? "cursor-pointer" : "",
          bg,
          text,
        ];
      }}
      eventContent={(arg: EventContentArg) => {
        const { start, end, extendedProps, title } = arg.event;
        const { status, assignee } =
          extendedProps as CalendarEvent["extendedProps"];

        return (
          <CalendarEventContent
            title={title}
            status={status}
            start={start}
            end={end}
            assignee={assignee}
          />
        );
      }}
    />
  );
};
